import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import { useHasPermission } from "@/common/acl/guard/guard";
import { Button } from "@/common/components/button/button";
import { Spinner } from "@/common/components/spinner/spinner";
import { toaster } from "@/common/components/toast/toast";
import { UserIdentity } from "@/common/components/user-identity/user-identity";

import { Table } from "../../components/table/table";
import { UserCompanyAction } from "../../components/user-actions/user-company-action";
import { UserRoleAction } from "../../components/user-actions/user-role-action";
import { UserStateAction } from "../../components/user-actions/user-state-action";
import {
  CompanyUser,
  companyUsersQuery,
  useAssignCompanyUserMutation,
} from "../company.api";
import { useCompanyId } from "../use-company-id";

const columnHelper = createColumnHelper<CompanyUser>();
const Users = () => {
  const id = useCompanyId();
  const hasCompanyApprovePermission = useHasPermission("users:company:approve");
  const hasUserCompanyDeletePermission = useHasPermission(
    "users:company:delete",
  );
  const hasCompanyRolePermission = useHasPermission("users:company:role");
  const hasCompanyAssignPermission = useHasPermission("users:company:assign");
  const users = useSuspenseQuery(companyUsersQuery({ id: parseInt(id) }));
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("id", {}),
      columnHelper.accessor("userId", {}),
      columnHelper.accessor("role", {}),
      columnHelper.accessor("userEmail", {
        cell: (info) => (
          <Link
            className="flex gap-2 text-can-forest-teal"
            to={`../../users/${info.row.getValue("userId")}`}
          >
            <UserIdentity identity={info.row.getValue("userId")} />
            {info.getValue()}
          </Link>
        ),
        header: () => t`E-mail`,
        meta: {
          label: () => t`E-mail`,
        },
      }),
      columnHelper.accessor("approvalState", {
        header: () => t`Stav`,
        meta: {
          label: () => t`Stav`,
        },
      }),

      columnHelper.display({
        cell: hasCompanyApprovePermission
          ? ({ row }) => (
              <UserStateAction
                initialStatus={row.getValue("approvalState")}
                userCompanyRelationId={row.getValue("id")}
              />
            )
          : ({ row }) => row.getValue("approvalState"),
        header: () => t`Stav`,
        id: "state-action",
      }),
      columnHelper.display({
        cell: hasCompanyRolePermission
          ? ({ row }) => (
              <UserRoleAction
                initialRole={row.getValue("role")}
                userCompanyRelationId={row.getValue("id")}
              />
            )
          : ({ row }) => row.getValue("role"),
        header: () => t`Role`,
        id: "role-action",
      }),
      ...(hasUserCompanyDeletePermission
        ? [
            columnHelper.display({
              cell: ({ row }) => (
                <UserCompanyAction userCompanyRelationId={row.getValue("id")} />
              ),
              header: () => t`Odebrat`,
              id: "user-company-action",
            }),
          ]
        : []),
    ],

    [
      hasCompanyApprovePermission,
      hasCompanyRolePermission,
      hasUserCompanyDeletePermission,
    ],
  );
  const userTable = useReactTable({
    columns,
    data: users.data,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        approvalState: false,
        id: false,
        role: false,
        userId: false,
      },
    },
  });
  return (
    <details>
      <summary>
        <h2 className="inline text-lg font-bold">
          <Trans>Uživatelé</Trans> ({users.data.length})
        </h2>
      </summary>
      {hasCompanyAssignPermission ? (
        <AssignUserForm companyId={parseInt(id)} />
      ) : null}
      {users.data.length > 0 ? (
        <Table>
          <Table.Head table={userTable} />
          <Table.Body table={userTable} />
        </Table>
      ) : null}
    </details>
  );
};

type AssignUserFormData = {
  email: string;
};

const AssignUserForm = ({ companyId }: { companyId: number }) => {
  const queryClient = useQueryClient();
  const assignUserMutation = useAssignCompanyUserMutation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<AssignUserFormData>();

  const onSubmit = React.useCallback(
    (data: AssignUserFormData) => {
      assignUserMutation.mutate(
        {
          companyId,
          companyUserAssignRequest: {
            email: data.email.trim(),
            role: "USER",
            state: "APPROVED",
          },
        },
        {
          onError: () => {
            toaster.error(
              {
                title: <Trans>Nepodařilo se přiřadit uživatele</Trans>,
              },
              {
                autoClose: 2000,
              },
            );
          },
          onSuccess: () => {
            void queryClient.invalidateQueries();
            reset();
          },
        },
      );
    },
    [assignUserMutation, companyId, queryClient, reset],
  );

  return (
    <form className="mb-4 flex gap-2" onSubmit={handleSubmit(onSubmit)}>
      <input
        type="email"
        {...register("email", { required: true })}
        className="border border-gray-300 rounded px-3 py-2 flex-1"
        placeholder={t`E-mail`}
      />
      <Button disabled={isSubmitting} type="submit" variant="primary">
        {isSubmitting ? <Spinner withHint={false} /> : <Trans>Přiřadit</Trans>}
      </Button>
    </form>
  );
};

export { Users };
